<script setup lang="ts">
import type { Ref, WatchStopHandle } from "vue";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTippy } from "vue-tippy";

import { cardActions, stickerChipId } from "@/action/cardActions";
import { stickyNoteAttributeChipClicked } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import StickerSelector from "@/components/StickerSelector/StickerSelector.vue";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import { stickerName } from "@/components/ui/BaseSticker/baseSticker";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Sticker } from "@/model/StickerType";
import { stickerSvgIcon } from "@/model/StickerType";
import { useBoardStore } from "@/store/board";
import { cardColor } from "@/store/card";
import { useDraggingStore } from "@/store/dragging";
import { useZoomStore } from "@/store/zoom";
import { isDarkColor } from "@/utils/color";
import { injectStrict } from "@/utils/vue";

const { t } = useI18n();

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const board = injectStrict(boardKey);

const triggerRef = useTemplateRef("trigger");
const dropdownRef = useTemplateRef("dropdown");
const tippyRef = ref<ReturnType<typeof useTippy>>();

const dragWatcher: Ref<WatchStopHandle | null> = ref(null);
const stickyWasDragged = ref(false);

const theme = computed(() => (isDarkColor(cardColor(card, board.value)) ? "dark" : "light"));
const sticker = computed(() => card.flagType);
const iconName = computed(() => (sticker.value ? stickerSvgIcon[sticker.value.icon] : null));
const colorName = computed(() => sticker.value?.color);
const isActive = computed(() => sticker.value !== null);
const cardIsActive = computed(() => useBoardStore().activeCardId === card.id);
const tooltipText = computed(() =>
  sticker.value ? stickerName(sticker.value) : t("general.stickers"),
);
const disabled = computed(() => cardMeta.isReadonly || stickyWasDragged.value);

onMounted(() => {
  tippyRef.value = useTippy(triggerRef, {
    content: tooltipText.value,
    delay: [500, 0],
    animation: false,
    aria: { content: null },
  });
});

watch(tooltipText, (val) => tippyRef.value?.setContent(val));

function handleStickerChange(sticker: Sticker | null) {
  cardActions.setFlag("action-menu", card, sticker);
  closeDropdown();
}

function closeDropdown() {
  dropdownRef.value?.close();
}

let zoomingWatcher: WatchStopHandle | undefined;
let boardChangeWatcher: WatchStopHandle | undefined;

function handleOpen() {
  const stickyType = card.type.functionality;
  // Keep "flag-chip" event name to preserve analytics continuity.
  trackEvent(stickyNoteAttributeChipClicked("flag-chip", stickyType, cardIsActive.value));

  zoomingWatcher = watch(() => useZoomStore().zooming, closeDropdown);
  boardChangeWatcher = watch(() => useBoardStore().board?.id, closeDropdown, { once: true });
}

function handleClose() {
  zoomingWatcher?.();
  boardChangeWatcher?.();
  dragWatcher.value?.();
  dragWatcher.value = null;
  tippyRef.value?.enable();
}

function handleDropdownPointerDown() {
  tippyRef.value?.disable();

  dragWatcher.value = watch(
    () => useDraggingStore().dragging[card.id],
    (isDragging) => {
      if (!isDragging) return;
      stickyWasDragged.value = true;
      closeDropdown();
    },
  );
}

function handleDropdownClick() {
  dragWatcher.value?.();
  tippyRef.value?.enable();

  if (stickyWasDragged.value) {
    stickyWasDragged.value = false;
    return;
  }
}
</script>

<template>
  <div class="sticker-chip">
    <DropdownMenu
      ref="dropdown"
      :disabled
      width="md"
      max-height="lg"
      stay-open-on-content-click
      @click.stop="handleDropdownClick"
      @pointerdown="handleDropdownPointerDown"
      @open="handleOpen"
      @close="handleClose"
    >
      <template #trigger="{ isOpen }">
        <button
          :id="stickerChipId(card.id)"
          ref="trigger"
          class="trigger"
          :class="[{ active: isActive, disabled }, theme, isActive && colorName ? colorName : null]"
          :tabindex="cardIsActive ? 0 : -1"
          :aria-label="tooltipText"
          :aria-expanded="isOpen"
          aria-haspopup="listbox"
        >
          <SvgIcon v-if="iconName" :name="iconName" />
          <SvgIcon v-else name="general/sticker" />
        </button>
      </template>
      <StickerSelector
        v-focus-trap="{
          fallbackFocus: triggerRef,
          setReturnFocus: `[id='${stickerChipId(card.id)}']`,
        }"
        data-ignore-click
        :selected="sticker"
        @keydown.tab.stop
        @select="handleStickerChange"
      />
    </DropdownMenu>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";

.sticker-chip {
  max-height: 100%;

  .tooltip {
    display: inline-block;
    height: 100%;
  }

  .trigger {
    display: grid;
    align-items: center;
    width: 20px;
    height: 20px;
    padding: 3px;
    border: 1px dashed currentcolor;
    border-radius: 50%;
    outline: revert;

    &:not(.disabled) {
      cursor: pointer;
    }

    &.light {
      color: colors-a11y.$outline-black;
    }

    &.dark {
      color: colors-a11y.$outline-white;
    }

    &:hover:not(.active) {
      &.light {
        color: colors.$palette-black-alpha-60;
      }

      &.dark {
        color: colors.$palette-white-alpha-80;
      }
    }

    &.active {
      border: none;
      padding: 0;
    }

    $sticker-colors: (
      blue: colors.$alternative-blue,
      green: colors.$alternative-green,
      orange: colors.$alternative-brown,
      purple: colors.$alternative-purple,
      red: colors.$alternative-red,
      yellow: colors.$alternative-yellow,
    );

    @each $color-name, $color-value in $sticker-colors {
      &.#{"#{$color-name}"} {
        color: $color-value;
      }
    }
  }
}
</style>
